import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ConfirmModal, { ConfirmModalHeader } from "PFComponents/confirm/confirm_modal";
import { LoadingDots } from "PFComponents/loading_dots";
import PageTabs from "PFComponents/page/page_tabs";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import Toggle from "PFComponents/toggle/toggle";
import Tooltip from "PFComponents/tooltip/tooltip";
import { fetchPaginatedCollection } from "PFCore/helpers/collection";
import { customTypesForProfile } from "PFCore/helpers/custom_types";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { ApiRoute } from "PFCore/utilities/routes";
import InfoIcon from "PFIcons/info.svg";
import { FeatureFlag, PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ExportableProfiles from "./helpers/exportable_profile";
import css from "./local_export_trigger.less";
import ExportTab from "./parts/export_tab";
import Fields from "./parts/fields";
import Overwrites from "./parts/overwrites";
import ProfilesTab from "./parts/profiles_tab";
import Spinner from "./parts/spinner";
import TemplateTab from "./parts/template_tab";
import TooltipInfo from "./parts/tooltip_info";
import useDownloadCsv from "./use_download_csv";
import useDownloadProfiles from "./use_download_profiles";
import useProfileLogo from "./use_profile_logo";

const PAGINATION_SIZE = 50;

const LocalExportTrigger = ({
  profilesEntity,
  rawDataCollection,
  initModalShown,
  handleModalClose,
  getProfilesCollection,
  isBlindModeOn,
  icon,
  label,
  buttonKind
}) => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState(
    profilesEntity?.entries.map(({ profile, _score }) => ({
      ...profile,
      score: _score
    }))
  );
  const rawData = rawDataCollection?.entries;
  const [page, setPage] = useState(profilesEntity?.meta?.page || 1);
  const [exportRawData, setExportRawData] = useState(true);
  const [exportProfiles, setExportProfiles] = useState(true);

  const [modalShown, setModalShown] = useState(!!initModalShown);
  const [areYouSureObject, setAreYouSureObject] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [tabsDisabled, setTabsDisabled] = useState(true);
  const [tab, setTab] = useState("template");

  const { currentAccount } = useContext(AppContext).store;
  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();

  const isEnabled = useIsFeatureEnabled();

  const [allTemplates, setAllTemplates] = useState([]);
  const [template, setTemplate] = useState(null);

  const [profileIndex, setProfileIndex] = useState(0);
  const [exportableProfiles, setExportableProfiles] = useState(null);

  const onDownload = () => setAreYouSureObject({ downloaded: true });
  const exportableProfile = exportableProfiles?.filterEnabled()?.[profileIndex];

  const { formatAndDownload } = useDownloadCsv();

  const defaultConfig = {
    exportTemplate: null,
    templateKey: null,
    summary: true,
    showAvatar: true,
    color: "#2358F8",
    color2: "#4B75FF",
    customTypes: [],
    hidden: { website: !isEnabled(FeatureFlag.Website) },
    order: [],
    removeEmptyColumns: false,
    isBlindModeOn: isBlindModeOn
  };

  const [config, setConfig] = useState(defaultConfig);

  const { logo, avatar } = useProfileLogo(config, exportableProfile);
  const {
    handleDownload: handleDownloadProfiles,
    downloadCsv,
    serializedProfile
  } = useDownloadProfiles(onDownload, config, exportableProfiles, exportableProfile, logo);

  const availableCustomTypes = useMemo(
    () =>
      profiles
        .map((profile) => customTypesForProfile(profile, currentProfile, customTypes))
        .reduce((acc, fields) => {
          fields.forEach((field) => {
            if (!acc.find(({ name }) => field.name === name)) {
              acc.push(field);
            }
          });
          return acc;
        }, [])
        .filter(({ name }) => (template?.fields || []).includes(name))
        .filter(({ sections }) =>
          sections.every(
            ({ key }) => key !== "current_experience_title_1" && key !== "current_experience_title_2"
          )
        ),
    [profiles, template]
  );

  useEffect(() => {
    setProfiles(
      profilesEntity?.entries.map(({ profile, _score }) => ({
        ...profile,
        score: _score
      }))
    );
  }, [profilesEntity]);

  useEffect(() => {
    fetchPaginatedCollection(ApiRoute(`/api/cv_templates`), {
      account_id: currentAccount.id,
      apiVersion: 2
    }).then((response) => setAllTemplates(response.entries));
  }, []);

  useEffect(() => {
    if (template) {
      const order = (template?.fields || []).filter((field) =>
        ["summary", "experience", ...availableCustomTypes.map((item) => item.name)].includes(field)
      );
      setConfig({
        ...defaultConfig,
        exportTemplate: template.export_format,
        templateKey: template.key,
        customTypes: availableCustomTypes,
        order
      });
    }
  }, [template, availableCustomTypes]);

  useEffect(() => {
    setExportableProfiles(new ExportableProfiles(profiles, customTypes));
  }, [profiles]);

  useEffect(() => {
    setTabsDisabled(!template || exportableProfiles?.filterEnabled().length === 0);
  }, [exportableProfiles, template]);

  const isMultipleProfiles = profiles.length > 1;
  const isPptx = template?.export_format === "pptx";
  const totalPages = Math.ceil(profilesEntity?.meta?.total / PAGINATION_SIZE) || 1;
  const canExportDocs = useIsCurrentUserPermittedTo(PermissionRule.ExportDocuments);
  const canExportRawData = !!rawDataCollection;

  const showExportButton =
    canExportRawData &&
    ((exportRawData && rawData.length > 0) || (exportProfiles && profilesEntity.meta.total > 0));

  const tabs = [
    {
      id: "template",
      displayElement: t("export.tabs.template")
    }
  ];
  if (isMultipleProfiles) {
    tabs.push({ id: "profiles", displayElement: t("export.tabs.profiles") });
  }
  if (!isPptx && template && template.fields.length > 0) {
    tabs.push({
      id: "fields",
      displayElement: t("export.tabs.fields"),
      disabled: tabsDisabled
    });
  }
  tabs.push({
    id: "overwrites",
    displayElement: t("export.tabs.exportEdit"),
    disabled: tabsDisabled
  });
  tabs.push({
    id: "export",
    displayElement: canExportRawData ? t("export.tabs.preview") : t("export.tabs.export"),
    disabled: tabsDisabled
  });

  if (!canExportDocs) {
    return false;
  }

  return (
    <>
      <Button
        icon={icon}
        text={label || (!icon && t("export.export"))}
        kind={buttonKind || "blank"}
        onClick={() => setModalShown(true)}
      />

      {areYouSureObject.active && (
        <ConfirmModal
          key="exportShowSureToCloseModal"
          handleClose={() => setAreYouSureObject({})}
          handleOK={() => areYouSureObject.ok()}
          labelOK={t("export.closeAnyway")}
          kindOK="danger"
        >
          <ConfirmModalHeader>{t("areYouSure")}</ConfirmModalHeader>
          <p style={{ marginTop: 10 }}>{t("export.closingWizardMessage", { count: profiles.length })}</p>
        </ConfirmModal>
      )}

      {modalShown && (
        <ConfirmModal
          key="exportMainModal"
          handleBeforeClose={(closeFn) => {
            if (areYouSureObject.downloaded) {
              return true;
            }
            setAreYouSureObject({ active: true, ok: () => closeFn() });
            return false;
          }}
          handleOK={() => {
            if (exportRawData) {
              formatAndDownload(
                "Table data.csv",
                Object.keys(rawData?.[0]),
                rawData.map((row) => Object.values(row))
              );
            }
            if (exportProfiles) {
              handleDownloadProfiles(true);
            }
            setModalShown(false);
            handleModalClose && handleModalClose();
          }}
          labelOK={t("export.export")}
          handleClose={() => {
            setModalShown(false);
            setAreYouSureObject({});
            handleModalClose && handleModalClose();
          }}
          showOKButton={showExportButton}
          executeCloseOnOk={!showExportButton}
          style={{ maxWidth: 1100, padding: "0 10px", marginTop: "calc(-10% + 20px)" }}
        >
          <ConfirmModalHeader>
            {canExportRawData ? (
              t("export.export")
            ) : (
              <>
                {t("export.exportProfiles", { count: profiles.length })}{" "}
                <Tooltip
                  content={<TooltipInfo isSingle={exportableProfiles?.length === 1} hasFields={!isPptx} />}
                  trigger="click"
                >
                  <span className={css.pointer}>
                    <InfoIcon height={20} width={20} viewBox="0 -3 20 20" />
                  </span>
                </Tooltip>
              </>
            )}
          </ConfirmModalHeader>
          {canExportRawData && (
            <>
              <ToggleRow
                checked={exportRawData}
                count={t("export.toggles.rowsAmount", { number: rawData.length })}
                label={t("export.toggles.tableData")}
                handleChange={setExportRawData}
              />
              <ToggleRow
                count={profilesEntity.meta.total}
                label={t("export.toggles.profiles")}
                handleChange={setExportProfiles}
                checked={exportProfiles}
              />
            </>
          )}

          {(exportProfiles || !canExportRawData) && (
            <>
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <PageTabs handleClick={({ id }) => setTab(id)} items={tabs} active={tab} />
              </div>

              {tab === "template" && (
                <TemplateTab allTemplates={allTemplates} template={template} onTemplateChange={setTemplate} />
              )}

              {tab === "profiles" && (
                <>
                  {isLoading && (
                    <div className={css.isLoading}>
                      <LoadingDots circlesEnabled />
                    </div>
                  )}
                  <div className={classNames({ [css.loadingWrapper]: isLoading })}>
                    <ProfilesTab
                      exportableProfiles={exportableProfiles}
                      handleClick={() => {
                        setProfileIndex(0); // so we're not accidentaly out of range
                        setTabsDisabled(exportableProfiles?.filterEnabled().length === 0);
                      }}
                      isBlindModeOn={isBlindModeOn}
                    />
                    <SimplePagination
                      currentPage={page}
                      totalPages={totalPages}
                      handleClick={(paginationPage) => {
                        setIsLoading(true);
                        getProfilesCollection(paginationPage, PAGINATION_SIZE).then((resp) => {
                          setProfiles(resp.entries.map(({ profile }) => profile));
                          setPage(resp.meta.page);
                          setIsLoading(false);
                        });
                      }}
                    />
                  </div>
                </>
              )}

              {tab === "fields" && (
                <Fields
                  config={config}
                  setConfig={setConfig}
                  setOrder={(order) => setConfig((prev) => ({ ...prev, order }))}
                  defaultConfig={defaultConfig}
                />
              )}

              {tab === "overwrites" && (
                <div>
                  <Spinner
                    exportableProfiles={exportableProfiles}
                    index={profileIndex}
                    changeIndex={setProfileIndex}
                  />
                  <Overwrites
                    config={config}
                    exportableProfile={exportableProfiles.filterEnabled()[profileIndex]}
                  />
                </div>
              )}

              {tab === "export" && (
                <div className={css.preview}>
                  <Spinner
                    exportableProfiles={exportableProfiles}
                    index={profileIndex}
                    changeIndex={setProfileIndex}
                  />
                  <ExportTab
                    exportableProfiles={exportableProfiles}
                    exportableProfile={exportableProfiles.filterEnabled()[profileIndex]}
                    index={profileIndex}
                    config={config}
                    setConfig={setConfig}
                    avatar={avatar}
                    logo={logo}
                    handleDownload={handleDownloadProfiles}
                    downloadCsv={downloadCsv}
                    serializedProfile={serializedProfile}
                    previewMode={canExportRawData}
                  />
                </div>
              )}
            </>
          )}
        </ConfirmModal>
      )}
    </>
  );
};

const ToggleRow = ({ label, count, handleChange, checked }) => (
  <div className={css.toggleRow}>
    <Toggle onChange={handleChange} checked={checked} inline reversed />
    <div className={css.labelContainer}>
      {label}
      <div className={css.itemCount}>{`(${count})`}</div>
    </div>
  </div>
);

LocalExportTrigger.propTypes = {
  profilesEntity: PropTypes.shape({
    meta: PropTypes.shape({
      total: PropTypes.number,
      page: PropTypes.number
    }),
    entries: PropTypes.array
  }),
  rawDataCollection: PropTypes.shape({
    meta: PropTypes.shape({
      total: PropTypes.number,
      page: PropTypes.number
    }),
    entries: PropTypes.array
  }),
  getProfilesCollection: PropTypes.func,

  initModalShown: PropTypes.bool,
  handleModalClose: PropTypes.func,
  isBlindModeOn: PropTypes.bool,
  classes: PropTypes.object,
  icon: PropTypes.string,
  label: PropTypes.node,
  buttonKind: PropTypes.string
};

ToggleRow.propTypes = {
  label: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  checked: PropTypes.bool.isRequired
};

export default LocalExportTrigger;
